import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import { MDText } from 'i18n-react';
import moment from 'moment';
import Overlay from 'react-overlays/es/Overlay';
import DateSelectorPopup, { VIEWS } from './DateSelectorPopup';
import DateSelectorInput from './DateSelectorInput';
import { withLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';
import clickOutside from '@adsk/bim360-matrix-react-helpers/es/clickOutsideHOC';
import { isSameDate, DatePropType } from './helpers/common';
import './_DateSelector.scss';
import { ThemeConsumer, Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';

const mergeProps = (propKeys, defaultProps, specificProps) => {
  const mergedProps = propKeys.reduce((newProps, key) => {
    newProps[key] = key in specificProps ? specificProps[key] : defaultProps[key];

    return newProps;
  }, {});
  return mergedProps;
};

const getEndOfDayDate = date => {
  // makes sure we don't manipulate state or props
  const clonedDate = new Date(date);
  clonedDate.setHours(23, 59, 59, 99);
  return clonedDate;
};

const WrappedDateSelectorInput = withLocale(
  DateSelectorInput,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
const WrappedDateSelectorPopup = withLocale(
  clickOutside(DateSelectorPopup),
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
const popperConfig = {
  modifiers: {
    preventOverflow: {
      boundariesElement: 'viewport'
    }
  }
};
/**
 * DateSelector
 */
export class DateSelector extends PureComponent {
  /**
   * constructor
   */
  constructor(props) {
    super(props);

    this.state = {
      open: false,
      selectedAction: null,
      selectedValue: props.date || props.defaultDate,
      startDate: props.startDate || props.defaultStartDate,
      endDate: props.endDate || props.defaultEndDate
    };

    this.isControlled = this.isControlled.bind(this);
    this.isDateContolled = this.isDateContolled.bind(this);
    this.toggleOpen = this.toggleOpen.bind(this);
    this.onChange = this.onChange.bind(this);
    this.clear = this.clear.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.getDateState = this.getDateState.bind(this);
    this.getPopupProps = this.getPopupProps.bind(this);
    this.getInputProps = this.getInputProps.bind(this);
    this.onRangeValueChange = this.onRangeValueChange.bind(this);
    this.getControlledOnChangeValues = this.getControlledOnChangeValues.bind(this);
  }

  componentDidMount() {
    if (this.props.open || (this.props.defaultOpen && !this.props.disabled)) {
      this.setState({ open: true });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { selectedValue, startDate, endDate } = this.state;
    const { range } = this.props;
    if (range) {
      if (
        (this.isControlled('startDate', nextProps) &&
          !isSameDate(startDate, nextProps.startDate)) ||
        (this.isControlled('endDate', nextProps) && !isSameDate(endDate, nextProps.endDate))
      ) {
        this.setState(this.initializeDateStateObject(nextProps));
      }
    } else {
      if (this.isControlled('date', nextProps) && !isSameDate(selectedValue, nextProps.date)) {
        this.setState(this.initializeDateStateObject(nextProps));
      }
    }

    if (this.isControlled('open', nextProps)) {
      this.setState({ open: nextProps.open });
    } else if (range && nextProps.startDate && nextProps.endDate) {
      this.setState({ open: false });
    }
  }

  /**
   * componentDidUpdate state calculations
   */

  componentDidUpdate(prevProps, prevState) {
    const { selectedValue, startDate, endDate, open } = this.state;

    if (prevState.open && !open) {
      this.props.onClose();
    }

    if (
      !this.isDateContolled() &&
      (prevState.selectedValue !== selectedValue ||
        prevState.startDate !== startDate ||
        prevState.endDate !== endDate)
    ) {
      this.props.onChange({ selectedValue, startDate, endDate });
    }
  }

  /**
   * returns true if the given prop is a controlled prop (i.e. - supplied in the props), false otherwise
   */

  isControlled(prop, props = this.props) {
    return prop in props;
  }

  /**
   * return true if the date value prop (startDate/endDate for range, date for non-range) is controlled, false otherwise
   */
  isDateContolled() {
    if (this.props.range) {
      return this.isControlled('startDate') || this.isControlled('endDate');
    }
    return this.isControlled('date');
  }

  /**
   * set the state of selected date values
   */

  getDateState(selectedValue, startDate = null, endDate = null) {
    return {
      selectedValue,
      startDate,
      endDate
    };
  }

  /**
   * Initializes date-related state object
   */
  initializeDateStateObject(props) {
    const { date, startDate, endDate } = props;
    // Initialize SelectedValue
    if (date) {
      // for single-date-picker (this.props.range===false) this is used as the date state
      return this.getDateState(date, date);
    } else if (startDate && endDate) {
      const isStartBeforeEnd = moment(startDate) < moment(endDate);
      return this.getDateState(
        null,
        isStartBeforeEnd ? startDate : endDate,
        isStartBeforeEnd ? endDate : startDate
      );
    } else if (startDate || endDate) {
      return this.getDateState(null, startDate ? startDate : endDate, null);
    } else {
      return this.getDateState(null);
    }
  }

  /**
   * handle click outside the date picker
   */
  handleClickOutside() {
    if (!('open' in this.props)) {
      this.setState({ open: false });
    }
  }

  /**
   * Clears input value and selection
   */
  clear() {
    if (this.isDateContolled()) {
      this.props.onChange(this.getControlledOnChangeValues(null));
      return;
    }
    this.setState({
      endDate: null,
      startDate: null,
      selectedValue: null
    });
  }

  /**
   * Toggles the calender open/close state
   */

  toggleOpen() {
    if (this.isControlled('open')) {
      return;
    }
    this.setState({
      open: !this.state.open,
      selectedValue: this.props.range ? this.state.startDate : this.state.selectedValue
    });
  }

  onRangeValueChange(date) {
    const { open, allowSameStartAndEndDate } = this.props;
    if (this.isControlled('startDate') || this.isControlled('endDate')) {
      return;
    }
    if (this.state.endDate && this.state.startDate) {
      //reset the range
      this.setState({ startDate: date, endDate: null });
    } else if (!this.state.startDate) {
      // if no start date is set - set it
      this.setState({ startDate: date });
    } else {
      //there's a start date and no end date, set the end date
      if (!allowSameStartAndEndDate && isSameDate(moment(this.state.startDate), moment(date))) {
        //cannot select the same value for start and end
        return;
      }
      if (this.state.startDate > date) {
        const endDate = getEndOfDayDate(this.state.startDate);
        this.setState({
          startDate: date,
          endDate,
          open
        });
      } else {
        const endDate = getEndOfDayDate(date);
        this.setState({ endDate, open });
      }
    }
  }

  getControlledOnChangeValues(date) {
    const { range, startDate, endDate } = this.props;

    if (!range) {
      return { date };
    } else if (startDate && endDate) {
      return {
        startDate: date,
        endDate: null
      };
    } else if (!startDate) {
      return { startDate: date };
    } else if (startDate > date) {
      const endDate = getEndOfDayDate(startDate);
      return {
        startDate: date,
        endDate
      };
    } else {
      const endDate = getEndOfDayDate(date);
      return { startDate, endDate };
    }
  }

  /**
   * callback called when selecting a date in the calendar element
   * @param date - the selected date
   * @param selectable - boolean - whether the date is selectable
   */

  onChange({ date, selectable, view }) {
    const { open, range } = this.props;

    if (!selectable) {
      return;
    } else if (this.isDateContolled()) {
      this.props.onChange(this.getControlledOnChangeValues(date));
      if (!range && !this.isControlled('open') && view === VIEWS.DAYS) {
        // don't want to close popover whenever a range value is updated, only when the second one was updated
        this.setState({ open: false });
      }
    } else if (range) {
      this.onRangeValueChange(date);
    } else {
      if (this.isControlled('open')) {
        this.setState({ selectedValue: date, open });
      } else {
        const nextOpen = view !== VIEWS.DAYS; // close only when day has been chosen.
        this.setState({ selectedValue: date, open: nextOpen });
      }
    }
  }

  /**
   * returns the props to be sent to the DatePickerPopupWrapper element
   */

  getPopupProps(dateValues) {
    const { popupProps = {}, popupContainer, initialView } = this.props;
    return {
      clearDisabled: !this.state.selectedValue && !this.state.startDate && !this.state.endDate,
      ...popupProps,
      ...mergeProps(['date', 'startDate', 'endDate', 'range', 'locale'], this.props, popupProps),
      ...dateValues,
      onClear: this.clear,
      onChange: this.onChange,
      handleClickOutside: this.handleClickOutside,
      isDateControlled: this.isDateContolled(),
      eventTypes: 'click',
      popupContainer,
      initialView
    };
  }

  getInputProps(dateValues) {
    const { inputProps = {}} = this.props;

    const props = {
      ...inputProps,
      ...mergeProps(
        ['date', 'startDate', 'endDate', 'range', 'valid', 'locale'],
        this.props,
        inputProps
      ),
      ...dateValues,
      onClick: this.toggleOpen
    };
    return props;
  }

  /**
   * render
   */
  render() {
    const { className, style, popupContainer, renderInput } = this.props;
    const { selectedValue, startDate, endDate, open } = this.state;
    const dateValues = { selectedValue, startDate, endDate, open };

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div
            style={style}
            className={classNames('DateSelector', className, {
              [`DateSelector--${theme}`]: theme === Themes.ALLOY
            })}
          >
            {typeof renderInput === 'function' ? (
              renderInput({ ...dateValues, togglePopup: this.toggleOpen })
            ) : (
              <WrappedDateSelectorInput theme={theme} {...this.getInputProps(dateValues)} />
            )}
            <Overlay
              show={open}
              placement="bottom-start"
              target={this}
              container={popupContainer}
              flip={true}
              popperConfig={popperConfig}
            >
              {({ props, scheduleUpdate }) => {
                return (
                  <WrappedDateSelectorPopup
                    theme={theme}
                    overlayProps={props}
                    scheduleUpdate={scheduleUpdate}
                    rootClose={true}
                    target={this}
                    handleClickOutside={this.toggleOpen}
                    {...this.getPopupProps(dateValues)}
                  />
                );
              }}
            </Overlay>
          </div>
        )}
      </ThemeConsumer>
    );
  }
}

DateSelector.propTypes = {
  /**
   * Language locale
   */
  locale: PropTypes.string,
  /**
   * Flag indicating whether or not the input is in a valid or invalid state.
   */
  valid: PropTypes.bool,
  /**
   * Boolean for disabling the component
   */
  disabled: PropTypes.bool,
  /**
   * Flag whether the date picker is for a date range of a single value
   */
  range: PropTypes.bool,
  /**
   * A selected date for calendar with single date select (should not be used with range=true )
   */
  date: DatePropType,
  /**
   * The uncontrolled date for calendar with single date select
   */
  defaultDate: DatePropType,
  /**
   * A selected start date for a calendar with range data (should be used with range=true)
   */
  startDate: DatePropType,
  /**
   * the uncontrolled start date
   */
  defaultStartDate: DatePropType,
  /**
   * A selected end date for a calendar with range data (should be used with range=true)
   */
  endDate: DatePropType,
  /**
   * the uncontrolled end date
   */
  defaultEndDate: DatePropType,
  /**
   * A callback function for the event of closing the date picker
   */
  onClose: PropTypes.func,
  /**
   * A callbcack function called when the selected value has been changed
   */
  onChange: PropTypes.func,
  /**
   * flag indicating whether start and end date can be the same
   */
  allowSameStartAndEndDate: PropTypes.bool,
  /**
   * flag to force open state on the date selector popup
   */
  open: PropTypes.bool,
  /**
   * should the popup be open initially
   */
  defaultOpen: PropTypes.bool,
  /**
   * Sets the initial picker view.
   */
  initialView: PropTypes.oneOf(Object.values(VIEWS)),
  /**
   * Props for the dateSelectorInput component
   */
  inputProps: PropTypes.shape(DateSelectorInput.PropTypes),
  /**
   * Props for the dateSelectorPopup component
   */
  popupProps: PropTypes.shape(DateSelectorPopup.PropTypes),
  /**
   * optional style object for date selector
   */
  style: PropTypes.object,
  /**
   * optional additional class for the date selector
   */
  className: PropTypes.string,
  /**
   * get the container elemenet for the popup element
   */
  popupContainer: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * allows rendering a custom activating element instead of the current input
   * signature: ({ selectedValue, startDate, endDate, open, togglePopup }) => <InputReplacement/>
   */
  renderInput: PropTypes.func
};

DateSelector.defaultProps = {
  onClose: noop,
  onChange: noop,
  popupContainer: () => document.body,
  valid: true
};
export default withLocale(
  DateSelector,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
